import React from 'react'
import { FormField, FormSelectField } from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import { NameDto } from 'api/Common/Types'
import { CampaignRebateDto } from 'api/Trading-Accounts/Types/MarketingCampaignDetailDto'
import { Form, FormikErrors, FormikProps, withFormik } from 'formik'
import { FormSubmitValues } from 'utils/formValidation'
import { isNumeric } from 'utils/is-numeric'
import { v4 as uuid } from 'uuid'

type CampaignDetailsBusinessRuleProps = FormikProps<CampaignRebateConfigurationFormValues> &
  OuterProps
export type CampaignRebateConfiguration = CampaignRebateDto & { id: string }

export interface CampaignRebateConfigurationFormValues {
  campaignCalculationTypeId: number | undefined
  rebateValue: number | undefined
  minimumVolume: number
  rebateMultiplier: string
}

export const CampaignRebateConfigurationAccountTypeModalUI: React.FC<
  CampaignDetailsBusinessRuleProps
> = (props) => {
  const { setFieldValue, tradingAccountTypes, values } = props

  return (
    <Form className='space-y-4'>
      <div className='grid w-full gap-3'>
        <FormSelectField
          name='rebateValue'
          label='Account Type'
          value={values.rebateValue}
          options={tradingAccountTypes}
          onChange={(value) => setFieldValue('rebateValue', value ? Number(value) : undefined)}
        />
        <FormField name='rebateMultiplier' label='Multiplier' type='number' step='0.01' />
      </div>

      <div className='flex justify-end'>
        <Button type='submit'>Save</Button>
      </div>
    </Form>
  )
}

interface OuterProps {
  data?: CampaignRebateConfiguration
  rebates: CampaignRebateDto[]
  calculationTypeId: number | undefined
  tradingAccountTypes: NameDto[]
  calculationTypes: NameDto[]
  onSubmit(values?: FormSubmitValues<CampaignRebateConfiguration>): void
}

const dataToSubmit = (
  props: OuterProps,
  values: CampaignRebateConfigurationFormValues
): FormSubmitValues<CampaignRebateConfiguration | undefined> => {
  const { data, calculationTypes } = props

  const calculationType = calculationTypes.find((x) => x.id === values?.campaignCalculationTypeId)

  if (calculationType) {
    return {
      id: data?.id || uuid(),
      campaignCalculationTypeId: calculationType,
      rebateValue: values?.rebateValue ? Number.parseInt(values.rebateValue.toString(), 10) : 0,
      minimumVolume: 0,
      rebateMultiplier: Number.parseInt(values.rebateMultiplier, 10) || 0,
    }
  }

  return data
}

const initialState = (props: OuterProps) => {
  const { data, calculationTypeId } = props

  return {
    campaignCalculationTypeId: calculationTypeId || undefined,
    rebateValue: data?.rebateValue ? Number.parseInt(data?.rebateValue.toString(), 10) : undefined,
    minimumVolume: 0,
    rebateMultiplier: data?.rebateMultiplier?.toString() || '',
  }
}

export const CampaignRebateConfigurationAccountTypeModal = withFormik<
  OuterProps,
  CampaignRebateConfigurationFormValues
>({
  mapPropsToValues: (props) => {
    return initialState(props)
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit(dataToSubmit(props, values))
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values, props) => {
    const { data, rebates } = props
    const errors: FormikErrors<CampaignRebateConfigurationFormValues> = {}

    const rebateValue = values?.rebateValue
      ? Number.parseInt(values.rebateValue.toString(), 10)
      : -1

    if (!values.rebateValue) {
      errors.rebateValue = 'Required'
    }

    if (values.rebateValue && rebateValue < 0) {
      errors.rebateValue = `Tier value must be at least 0`
    }

    if (
      values.rebateValue &&
      rebates
        .filter((x) => x.rebateValue !== data?.rebateValue)
        .map((x) => x.rebateValue)
        .includes(rebateValue)
    ) {
      errors.rebateValue = 'This Account Type is already in use. Please choose a unique value'
    }

    if (!values.rebateMultiplier) {
      errors.rebateMultiplier = 'Required'
    }

    if (values.rebateValue && !isNumeric(values.rebateValue)) {
      errors.rebateValue = 'AccountType must be a numeric value. Please enter a number'
    }

    if (values.rebateMultiplier && !isNumeric(values.rebateMultiplier)) {
      errors.rebateMultiplier = 'Rebate multiplier must be a numeric value. Please enter a number'
    }

    return errors
  },
  enableReinitialize: true,
})(CampaignRebateConfigurationAccountTypeModalUI)
